/**
 * Which SBS-website form an inbound Webflow notification came from (#369).
 *
 * The sender cannot tell us this: every Webflow form notifies from the same bounce
 * domain, so `isWebflowFormEmail`'s sender check matches all of them equally. That is
 * why an "Exterior Improvements" assumption based on the sender quietly swallowed
 * violations, questionnaires and contact-us messages and tried to parse them as
 * improvement applications. The SUBJECT is the discriminator.
 *
 * Unrecognised subjects return 'unknown' rather than defaulting to the improvements
 * form — a new form added on the website must show up as unclassified for a human to
 * look at, not silently become an improvement request.
 */

import { normalizeReplySubject } from '@/utils/email-threading';

export type WebflowFormType =
	| 'exterior_improvement'
	| 'reported_violation'
	| 'owner_questionnaire'
	| 'request_documents'
	| 'maintenance_request'
	| 'contact_us'
	| 'bill_pay_signup'
	/**
	 * Not a website form at all — the type a human picks to say "this is just an email"
	 * (#465).
	 *
	 * Deliberately absent from `FORM_PATTERNS` below: no subject can produce it, because
	 * it exists only as a DESTINATION for someone correcting the classifier. Homeowners
	 * submit the wrong form regularly, and sometimes a form for work the company does not
	 * do at all; re-typing to this records the message against the owner's account without
	 * pretending a request came of it.
	 */
	| 'correspondence'
	| 'unknown';

/**
 * Subject patterns, most specific first. Matched against the reply/forward-stripped,
 * lower-cased, whitespace-collapsed subject.
 *
 * Anchored on the distinguishing words rather than the whole string, because Webflow
 * form names get edited on the website ("Maintenance Requests" vs "Maintenance
 * Request") and a wording tweak shouldn't silently reclassify a form as unknown.
 */
const FORM_PATTERNS: Array<[RegExp, WebflowFormType]> = [
	[/exterior improvements? application/, 'exterior_improvement'],
	[/reported violation/, 'reported_violation'],
	[/owner questionnaire/, 'owner_questionnaire'],
	[/request documents?/, 'request_documents'],
	[/maintenance requests?/, 'maintenance_request'],
	[/contact us/, 'contact_us'],
	// Hyphen tolerated: the app's own label spells it "Online Bill Pay Sign-Up", and a
	// subject written that way matched nothing and classified as `unknown`.
	[/bill pay sign[ -]?up/, 'bill_pay_signup'],
];

/** Every recognised subject ends this way; it's what marks a message as a form at all. */
const FORM_SUBMISSION_SUFFIX = /new form submission/;

export function classifyWebflowForm(subject: string | null | undefined): WebflowFormType {
	const s = normalizeReplySubject(subject);
	if (!s) return 'unknown';

	// Require the "new form submission" marker so a homeowner writing "contact us
	// about my deck" isn't filed as a Contact Us form.
	if (!FORM_SUBMISSION_SUFFIX.test(s)) return 'unknown';

	for (const [pattern, type] of FORM_PATTERNS) {
		if (pattern.test(s)) return type;
	}
	return 'unknown';
}

/**
 * True only for the Exterior Improvements application.
 *
 * This is the gate on the improvements intake: everything else is recorded as a form
 * submission against the owner's account instead.
 */
export const isExteriorImprovementForm = (subject: string | null | undefined): boolean =>
	classifyWebflowForm(subject) === 'exterior_improvement';

/**
 * How each form type reads in the app.
 *
 * `reported_violation` is shown as **Complaint** deliberately. The website form is called
 * "Reported Violation", but nothing here adjudicates a violation — the record is simply a
 * complaint somebody filed, and calling it a "violation" in the UI would assert a finding
 * that no one has made. The internal `form_type` keeps the form's own name so the
 * subject classifier and the website stay in step.
 */
export const WEBFLOW_FORM_LABEL: Record<WebflowFormType, string> = {
	exterior_improvement: 'Exterior Improvement Application',
	reported_violation: 'Complaint',
	owner_questionnaire: 'Owner Questionnaire',
	request_documents: 'Document Request',
	maintenance_request: 'Maintenance Request',
	contact_us: 'Contact Us',
	bill_pay_signup: 'Online Bill Pay Sign-Up',
	correspondence: 'Correspondence',
	unknown: 'Unclassified Form',
};

/** Ionicons glyph per form type, for the account-page section rows. */
export const WEBFLOW_FORM_ICON: Record<WebflowFormType, string> = {
	exterior_improvement: 'construct-outline',
	reported_violation: 'warning-outline',
	owner_questionnaire: 'clipboard-outline',
	request_documents: 'document-text-outline',
	maintenance_request: 'hammer-outline',
	contact_us: 'mail-outline',
	bill_pay_signup: 'card-outline',
	correspondence: 'mail-open-outline',
	unknown: 'help-circle-outline',
};
